// The League Play creation flow — the four wizard steps plus each flow's payoff
// screen.
//
// Client Rev 1 (Create Public League + Create Private League): these screens run
// as a FOCUSED flow. No QUSHi logo, no three-dot menu, no "League Detail" label
// and no bottom app navigation while the organiser is inside them — just one
// back arrow, the centred flow title, and the sticky CTA.
//
// The list lives here rather than inside the wizard because three different
// places have to agree on it: AuthLayout (bottom navigation), Contest (the
// DashboardHeader chrome + its top offset) and the wizard itself.
// Every screen of either creation flow renders its own focused header — one back
// arrow, and the flow title where the design has one — so the app's
// DashboardHeader (logo, three-dot menu, "League Detail") is suppressed for all
// of them. Leaving it on any one of them puts TWO back arrows on the screen.
export const LEAGUE_FOCUSED_PATHS = [
  "/contest/create-public-league",
  "/contest/create-private-league",
  "/contest/public-league-created",
  "/contest/private-league-created",
];

// The same set drives the no-bottom-navigation rule.
export const LEAGUE_CREATION_PATHS = [...LEAGUE_FOCUSED_PATHS];

// League Details, reached from the in-league three-dot menu (client Rev 3):
// "keep the user inside the league context — show a back arrow only, remove the
// main QUSHi bottom navigation and main site three dot menu."
//
// A pattern rather than a listed path, because the slug is part of it. It gets
// the same focused treatment as the creation flow: no DashboardHeader chrome and
// no bottom navigation, just the league's own content under one back arrow.
// League Settings is the create wizard reopened on an existing league, so client
// Rev 3 asks for it to look like the creation flow it is: "This path should show
// the same creation screens as we show when you come from the Create League CTA."
// Same treatment as details — own header, no app chrome, no bottom navigation.
const LEAGUE_DETAILS_PATTERN = /^\/contest\/[^/]+\/details\/?$/;
const LEAGUE_SETTINGS_PATTERN = /^\/contest\/[^/]+\/settings\/?$/;

// The details screen has a second address, for a visitor who is not in the
// league — /leaderboards/<slug>/details, reached from "Game details ›" on the
// standalone leaderboard. Client: "There is no CTA on a league or game preview
// only details page. its a focus screen with only the back arrow."
//
// "Only the back arrow" is precisely the focused treatment below, so the
// standalone address has to be recognised here too; without it the screen would
// keep the app header and the bottom bar and stop being a focus screen.
const STANDALONE_DETAILS_PATTERN = /^\/leaderboards\/[^/]+\/details\/?$/;

export const isLeagueDetailsPath = (pathname) =>
  LEAGUE_DETAILS_PATTERN.test(pathname || "") ||
  STANDALONE_DETAILS_PATTERN.test(pathname || "");

// League SETTINGS keeps the focused header; League DETAILS does not.
//
// They shared one pattern because both were "the league's own content under one
// back arrow". Settings still is — it is the create wizard reopened on an
// existing league, and the client asked for it to look like the flow it is.
//
// Details is not, any more. Client, on the landing page previews: "restore
// desktop header with QUSHi logo, notifications, and chats icons. Remove header
// divider line, delete arrow, move share icon down inline with the arrow…
// Mobile is the arrow QUSHi logo and share icon."
//
// That is the ordinary app header at both widths, so the details addresses come
// out of the focused set. They stay in `isLeagueCreationPath`, which is the
// separate no-bottom-navigation rule the client set earlier and did not revisit.
export const isLeagueFocusedPath = (pathname) =>
  LEAGUE_FOCUSED_PATHS.includes(pathname) ||
  LEAGUE_SETTINGS_PATTERN.test(pathname || "");

// The no-bottom-navigation rule, which is SEPARATE from the header one and
// unchanged by the header work above: details and settings both still suppress
// the bar. Settings has to be named explicitly now that it no longer rides on
// isLeagueDetailsPath.
export const isLeagueCreationPath = (pathname) =>
  LEAGUE_CREATION_PATHS.includes(pathname) ||
  isLeagueDetailsPath(pathname) ||
  LEAGUE_SETTINGS_PATTERN.test(pathname || "");

export default isLeagueCreationPath;
